import { mapSearchStateToAiSearchContext } from '@pkgs/components/utils/tracking';
import { useAuth } from '@ev-tech/user-account-modules';
import {
  type BusinessDivisionType,
  getAiSearchContext,
  getDivisionContext,
  getNavigationContext,
  getPageContext,
  getShopContext,
  trackNavigationEvent,
  trackUserInteractionEvent,
  UserActionEnum,
} from '@ev-tech/snowplow-library';

import { getConsentContext, useSearchModuleTracking } from './useSearchModuleTracking';
import { useIsLipaPage } from './useIsLipaPage';
import { useAppContext } from './useAppContext';
import { useSearchStore } from './stores/search/useSearchStore';
import { SearchMode } from './stores/search/slices/searchModeSlice';
import { PageType, TemplateType } from '../types/tracking';
import { useMapVisibilityStore } from '../providers/MapVisibilityStoreProvider';

export const useCreateSearchAlertButtonClick = () => {
  const isLipaPage = useIsLipaPage();
  const {
    countryCode,
    options: { language },
  } = useAppContext();
  const { isSignedIn } = useAuth();
  const masterDataShopIds = useSearchStore((state) => state.masterDataShopIds);
  const businessArea = useSearchStore((state) => state.objectTypeCategory);
  const preferences = useSearchStore((state) => state.preferences);
  const searchMode = useSearchStore((state) => state.searchMode);
  const isMapToggledOn = useMapVisibilityStore((state) => state.isMapActive);

  const { tracking } = useAppContext();
  const { mapABTestContext } = useSearchModuleTracking();

  function trackButtonClickNavigationEvent(elementId: string, targetScreen?: string, sourceScreen?: string) {
    trackNavigationEvent({
      screenName: '',
      contexts: [
        getNavigationContext({
          element_id: elementId,
          target_screen: targetScreen ?? 'search-alert_modal_form',
          ...(sourceScreen && { source_screen: sourceScreen }),
        }),
        getPageContext({
          isHQPage: true,
          type: tracking?.pageType ?? (isLipaPage ? PageType.LIPA_SEARCH_RESULT : PageType.SEARCH_RESULT),
          country_code: countryCode,
          page_language: language,
          template_type: tracking?.templateType ?? TemplateType.SEARCH_RESULT,
        }),
        getShopContext({ id: masterDataShopIds?.[0], name: businessArea }),
        getConsentContext(),
        getAiSearchContext(
          mapSearchStateToAiSearchContext({
            preferences,
            isAiSearchEnabled: searchMode === SearchMode.AI,
          }),
        ),
      ],
    });
  }

  function getSignInModalTrackingContexts() {
    return [
      getDivisionContext({
        name: businessArea as BusinessDivisionType,
      }),
      getAiSearchContext(
        mapSearchStateToAiSearchContext({
          preferences,
          isAiSearchEnabled: searchMode === SearchMode.AI,
        }),
      ),
      mapABTestContext(isMapToggledOn),
    ];
  }

  function trackButtonClickUserInteractionEvent(elementId: string) {
    if (!isSignedIn) return;

    const commonContexts = [
      getPageContext({
        isHQPage: true,
        type: tracking?.pageType ?? (isLipaPage ? PageType.LIPA_SEARCH_RESULT : PageType.SEARCH_RESULT),
        country_code: countryCode,
        page_language: language,
        template_type: tracking?.templateType ?? TemplateType.SEARCH_RESULT,
      }),
      getShopContext({ id: masterDataShopIds?.[0] }),
      ...getSignInModalTrackingContexts(),
    ];

    trackUserInteractionEvent({
      elementId,
      action: UserActionEnum.click,
      contexts: commonContexts,
    });
    trackButtonClickNavigationEvent(elementId);
  }

  return { getSignInModalTrackingContexts, trackButtonClickUserInteractionEvent, trackButtonClickNavigationEvent };
};
